import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import { Car, Phone, Mail, MapPin, Clock, CheckCircle, ChevronRight, Menu, X, Sparkles, Droplets, Shield, HelpCircle, ChevronDown, Instagram, FileText, ArrowLeft, CarFront, Truck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8f5d79e3-c8e6-419c-a9da-7e966a16ea6e/artifacts/f3d3npdu_18D14FD5-BD20-4CF2-928E-41594E01056C.png";

// Image URLs
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1588581952346-a6799a192088?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  interior: "https://images.unsplash.com/photo-1771491237218-cbd4a707497e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  exterior: "https://images.unsplash.com/photo-1695220369193-55601cc1ac02?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  gallery: [
    "https://images.unsplash.com/photo-1727940334409-36a3407d623a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    "https://images.unsplash.com/photo-1623000530355-249109e75eb5?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    "https://images.unsplash.com/photo-1641796234386-26d64fbedd7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    "https://images.unsplash.com/photo-1615506220358-1e0ae4270298?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
  ]
};

// Vehicle types with examples
const VEHICLE_TYPES = [
  {
    id: "small",
    name: "Small",
    examples: "Hatchback, small sedan e.g. Mazda 3, Toyota Corolla, VW Polo"
  },
  {
    id: "mid",
    name: "Medium",
    examples: "Mid-size sedan, small SUV e.g. Camry, RAV4, Hyundai Tucson"
  },
  {
    id: "xl",
    name: "Large",
    examples: "Large SUV, ute, wagon, van e.g. LandCruiser, HiLux, Kluger, Transit"
  }
];

// Services data with pricing tiers
const SERVICES_DATA = {
  small: [
    { id: "int-sml", name: "Interior Detail", price: 80, description: "Full interior steam clean, vacuum, all surfaces conditioned, windows, scent." },
    { id: "ext-sml", name: "Exterior Detail", price: 60, description: "Hand wash, bug/tar removal, tires & rims, ceramic sealant, windows, towel dry." },
    { id: "fd-sml", name: "Full Detail", price: 110, description: "Deluxe hand wash, full interior steam clean, all surfaces conditioned, polish, air freshener." },
    { id: "pc-sml", name: "Paint Correction", price: 300, description: "Professional machine polishing, clay bar treatment, full detail included." },
    { id: "cc-sml", name: "Ceramic Coating", price: 600, description: "4 year ceramic protection with full detail included." }
  ],
  mid: [
    { id: "int-med", name: "Interior Detail", price: 90, description: "Full interior steam clean, vacuum, all surfaces conditioned, windows, scent." },
    { id: "ext-med", name: "Exterior Detail", price: 70, description: "Hand wash, bug/tar removal, tires & rims, ceramic sealant, windows, towel dry." },
    { id: "fd-med", name: "Full Detail", price: 130, description: "Deluxe hand wash, full interior steam clean, all surfaces conditioned, polish, air freshener." },
    { id: "pc-med", name: "Paint Correction", price: 350, description: "Professional machine polishing, clay bar treatment, full detail included." },
    { id: "cc-med", name: "Ceramic Coating", price: 600, description: "4 year ceramic protection with full detail included." }
  ],
  xl: [
    { id: "int-4wd", name: "Interior Detail", price: 100, description: "Full interior steam clean, vacuum, all surfaces conditioned, windows, scent." },
    { id: "ext-4wd", name: "Exterior Detail", price: 80, description: "Hand wash, bug/tar removal, tires & rims, ceramic sealant, windows, towel dry." },
    { id: "fd-4wd", name: "Full Detail", price: 150, description: "Deluxe hand wash, full interior steam clean, all surfaces conditioned, polish, air freshener." },
    { id: "pc-4wd", name: "Paint Correction", price: 400, description: "Professional machine polishing, clay bar treatment, full detail included." },
    { id: "cc-4wd", name: "Ceramic Coating", price: 600, description: "4 year ceramic protection with full detail included." }
  ]
};

// Service details
const SERVICE_DETAILS = {
  interior: [
    "Full vacuum (seats, floors, trunk)",
    "Deep clean of all surfaces, trim, cup holders, vents",
    "Interior windows",
    "Door jambs and floor mats",
    "Scent spray"
  ],
  exterior: [
    "Hand wash and rinse (two-bucket method)",
    "Bug and tar removal",
    "Tire and rim clean + shine",
    "Ceramic sealant and gloss protection",
    "Exterior windows and mirrors",
    "Towel dry and detail finish"
  ]
};

// Add-ons data
const ADDONS = [
  { id: "clay-bar", name: "Clay Bar Treatment", price: 40, description: "Removes embedded contaminants for a glass-smooth finish. 30-40 min." },
  { id: "paint-correction", name: "Paint Correction", prices: { small: 300, mid: 350, xl: 400 }, description: "Professional machine polishing to remove swirl marks and scratches." },
  { id: "ceramic-coating", name: "Ceramic Coating", price: 600, description: "Long-lasting ceramic protection for your vehicle's paintwork." }
];

// Services data
const SERVICES = [
  {
    id: "full",
    name: "Full Detail",
    price: 130,
    duration: 180,
    description: "Complete interior and exterior rejuvenation. The ultimate car care package.",
    icon: Sparkles,
    featured: true
  },
  {
    id: "interior",
    name: "Interior Only",
    price: 80,
    duration: 90,
    description: "Full vacuum, deep clean all surfaces, interior windows, door jambs, scent spray.",
    icon: Car
  },
  {
    id: "exterior",
    name: "Exterior Only",
    price: 80,
    duration: 90,
    description: "Hand wash, bug & tar removal, tire & rim shine, windows, towel dry finish.",
    icon: Droplets
  }
];

// Navigation Component
const Navigation = ({ scrollToSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", section: "services" },
    { name: "About", section: "about" },
    { name: "FAQ", section: "faq" },
    { name: "Contact", section: "contact" }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-lr-black/95 backdrop-blur-md border-b border-lr-border" : "bg-transparent"
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#hero" className="flex items-center" data-testid="nav-logo">
            <img src={LOGO_URL} alt="LR Detail Sydney" className="h-10 md:h-12 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.section}
                onClick={() => scrollToSection(link.section)}
                className="nav-link text-lr-silver hover:text-white font-manrope text-sm tracking-wide transition-colors"
                data-testid={`nav-${link.section}`}
              >
                {link.name}
              </button>
            ))}
            <Button 
              onClick={() => scrollToSection("booking")}
              className="bg-lr-red hover:bg-red-700 text-white font-barlow uppercase tracking-wider px-6 btn-glow"
              data-testid="nav-book-now"
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-lr-black/98 backdrop-blur-md border-t border-lr-border" data-testid="mobile-menu">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.section}
                  onClick={() => {
                    scrollToSection(link.section);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-lr-silver hover:text-white font-manrope py-2 transition-colors"
                  data-testid={`mobile-nav-${link.section}`}
                >
                  {link.name}
                </button>
              ))}
              <Button 
                onClick={() => {
                  scrollToSection("booking");
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-lr-red hover:bg-red-700 text-white font-barlow uppercase tracking-wider mt-4"
                data-testid="mobile-book-now"
              >
                Book Now
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ scrollToSection }) => {
  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${IMAGES.hero})` }}
      />
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="animate-fade-in-up">
          <img 
            src={LOGO_URL} 
            alt="LR Detail Sydney" 
            className="h-64 md:h-80 lg:h-96 w-auto mx-auto mb-8"
            data-testid="hero-logo"
          />
          <h1 className="font-barlow text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 tracking-wide">
            PREMIUM MOBILE DETAILING
          </h1>
          <p className="text-lr-silver text-lg md:text-xl font-manrope mb-2">
            Professional car care servicing Parramatta and Ryde council
          </p>
          <p className="text-lr-red text-xl md:text-2xl font-barlow font-semibold mb-10 tracking-wider">
            WE COME TO YOU
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              variant="outline"
              onClick={() => scrollToSection("services")}
              className="border-lr-silver text-white hover:bg-white/10 font-barlow uppercase tracking-wider px-10 py-6 text-lg"
              data-testid="hero-view-services"
            >
              View Services
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = ({ scrollToSection }) => {
  const PriceBox = ({ price, label }) => (
    <div className="border-2 border-lr-red p-2 text-center">
      <div className="font-barlow font-black text-white leading-none">
        <span className="text-base align-top">$</span>
        <span className="text-3xl">{price}</span>
      </div>
      <div className="font-barlow font-bold text-xs text-lr-silver mt-1 uppercase">{label}</div>
    </div>
  );

  const Feature = ({ text }) => (
    <li className="flex items-start gap-2">
      <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={14} />
      <span className="font-manrope font-semibold text-xs text-lr-silver uppercase tracking-wide">{text}</span>
    </li>
  );

  return (
    <section id="services" className="py-20 md:py-32 bg-lr-black" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            OUR SERVICES
          </h2>
        </div>

        {/* FULL DETAIL - flagship card on top */}
        <div className="max-w-md mx-auto mb-8">
          <div className="bg-lr-card border border-lr-border rounded-sm overflow-hidden" data-testid="service-card-full-detail">
            <div className="p-5 md:p-6">
              <h3 className="font-barlow text-2xl md:text-3xl text-center tracking-wide">
                <span className="font-black text-lr-red">FULL </span>
                <span className="font-black text-white">DETAIL</span>
              </h3>
              <p className="text-lr-red font-barlow font-bold text-center text-xs tracking-[0.2em] mt-1 mb-4">
                DELUXE HAND WASH AND DETAIL
              </p>

              <ul className="space-y-1.5 mb-4">
                <Feature text="Foam Wash & Pre Wash" />
                <Feature text="Deluxe Hand Wash & Dry" />
                <Feature text="Complete Interior Steam Clean & Vacuum" />
                <Feature text="All Surfaces Cleaned & Conditioned" />
                <Feature text="Windows, Mirrors & Door Jambs" />
                <Feature text="Tire & Rim Clean + Shine" />
                <Feature text="Polish" />
                <Feature text="Air Freshener Included" />
              </ul>

              <div className="grid grid-cols-3 gap-2">
                <PriceBox price={110} label="SML" />
                <PriceBox price={130} label="MED" />
                <PriceBox price={150} label="4WD SUV" />
              </div>
            </div>
            <button
              onClick={() => scrollToSection("booking", "fd")}
              className="w-full bg-lr-red text-white font-barlow font-bold py-3 text-lg uppercase tracking-[0.15em] hover:bg-red-700 transition-colors"
              data-testid="book-full-detail"
            >
              BOOK ONLINE
            </button>
          </div>
        </div>

        {/* INTERIOR + EXTERIOR - smaller cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
          {/* INTERIOR DETAIL Card */}
          <div className="bg-lr-card border border-lr-border rounded-sm overflow-hidden" data-testid="service-card-interior-detail">
            <div className="p-4 md:p-5">
              <h3 className="font-barlow text-xl md:text-2xl text-center tracking-wide">
                <span className="font-black text-lr-red">INTERIOR </span>
                <span className="font-black text-white">DETAIL</span>
              </h3>
              <p className="text-lr-red font-barlow font-bold text-center text-[10px] tracking-[0.2em] mt-1 mb-3">
                STEAM CLEAN & CONDITION
              </p>

              <ul className="space-y-1.5 mb-4">
                <Feature text="Full Vacuum (Seats, Floors, Boot)" />
                <Feature text="Interior Steam Clean" />
                <Feature text="Dashboard, Trim & Vents Cleaned" />
                <Feature text="All Surfaces Conditioned" />
                <Feature text="Interior Windows & Mirrors" />
                <Feature text="Door Jambs & Floor Mats" />
                <Feature text="Scent Spray / Air Freshener" />
              </ul>

              <div className="grid grid-cols-3 gap-2">
                <div className="border-2 border-lr-red p-1.5 text-center">
                  <div className="font-barlow font-black text-white leading-none">
                    <span className="text-xs align-top">$</span>
                    <span className="text-2xl">80</span>
                  </div>
                  <div className="font-barlow font-bold text-[10px] text-lr-silver mt-1 uppercase">SML</div>
                </div>
                <div className="border-2 border-lr-red p-1.5 text-center">
                  <div className="font-barlow font-black text-white leading-none">
                    <span className="text-xs align-top">$</span>
                    <span className="text-2xl">90</span>
                  </div>
                  <div className="font-barlow font-bold text-[10px] text-lr-silver mt-1 uppercase">MED</div>
                </div>
                <div className="border-2 border-lr-red p-1.5 text-center">
                  <div className="font-barlow font-black text-white leading-none">
                    <span className="text-xs align-top">$</span>
                    <span className="text-2xl">100</span>
                  </div>
                  <div className="font-barlow font-bold text-[10px] text-lr-silver mt-1 uppercase">4WD SUV</div>
                </div>
              </div>
            </div>
            <button
              onClick={() => scrollToSection("booking", "int")}
              className="w-full bg-lr-red text-white font-barlow font-bold py-2.5 text-sm uppercase tracking-[0.15em] hover:bg-red-700 transition-colors"
              data-testid="book-interior-detail"
            >
              BOOK ONLINE
            </button>
          </div>

          {/* EXTERIOR DETAIL Card */}
          <div className="bg-lr-card border border-lr-border rounded-sm overflow-hidden" data-testid="service-card-exterior-detail">
            <div className="p-4 md:p-5">
              <h3 className="font-barlow text-xl md:text-2xl text-center tracking-wide">
                <span className="font-black text-lr-red">EXTERIOR </span>
                <span className="font-black text-white">DETAIL</span>
              </h3>
              <p className="text-lr-red font-barlow font-bold text-center text-[10px] tracking-[0.2em] mt-1 mb-3">
                HAND WASH & SEAL
              </p>

              <ul className="space-y-1.5 mb-4">
                <Feature text="Foam Wash & Pre Wash" />
                <Feature text="Two-Bucket Hand Wash & Rinse" />
                <Feature text="Bug & Tar Removal" />
                <Feature text="Tire & Rim Clean + Shine" />
                <Feature text="Ceramic Sealant & Gloss Protection" />
                <Feature text="Exterior Windows & Mirrors" />
                <Feature text="Towel Dry & Detail Finish" />
              </ul>

              <div className="grid grid-cols-3 gap-2">
                <div className="border-2 border-lr-red p-1.5 text-center">
                  <div className="font-barlow font-black text-white leading-none">
                    <span className="text-xs align-top">$</span>
                    <span className="text-2xl">60</span>
                  </div>
                  <div className="font-barlow font-bold text-[10px] text-lr-silver mt-1 uppercase">SML</div>
                </div>
                <div className="border-2 border-lr-red p-1.5 text-center">
                  <div className="font-barlow font-black text-white leading-none">
                    <span className="text-xs align-top">$</span>
                    <span className="text-2xl">70</span>
                  </div>
                  <div className="font-barlow font-bold text-[10px] text-lr-silver mt-1 uppercase">MED</div>
                </div>
                <div className="border-2 border-lr-red p-1.5 text-center">
                  <div className="font-barlow font-black text-white leading-none">
                    <span className="text-xs align-top">$</span>
                    <span className="text-2xl">80</span>
                  </div>
                  <div className="font-barlow font-bold text-[10px] text-lr-silver mt-1 uppercase">4WD SUV</div>
                </div>
              </div>
            </div>
            <button
              onClick={() => scrollToSection("booking", "ext")}
              className="w-full bg-lr-red text-white font-barlow font-bold py-2.5 text-sm uppercase tracking-[0.15em] hover:bg-red-700 transition-colors"
              data-testid="book-exterior-detail"
            >
              BOOK ONLINE
            </button>
          </div>
        </div>

        {/* PAINT CORRECTION + CERAMIC COATING */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
          {/* PAINT CORRECTION Card */}
          <div className="bg-lr-card border border-lr-border rounded-sm overflow-hidden" data-testid="service-card-paint-correction">
            <div className="p-5 md:p-6">
              <h3 className="font-barlow text-2xl md:text-3xl text-center tracking-wide">
                <span className="font-black text-lr-red">PAINT </span>
                <span className="font-black text-white">CORRECTION</span>
              </h3>
              <p className="text-lr-red font-barlow font-bold text-center text-xs tracking-[0.2em] mt-1 mb-4">
                FULL DETAIL INCLUDED
              </p>
              
              <ul className="space-y-1.5 mb-4">
                <Feature text="Professional Machine Polishing" />
                <Feature text="Swirl Mark & Scratch Removal" />
                <Feature text="Paint Oxidation Correction" />
                <Feature text="Clay Bar Treatment" />
                <Feature text="Full Detail Service Included" />
              </ul>
              
              <div className="grid grid-cols-3 gap-2">
                <PriceBox price={300} label="SML" />
                <PriceBox price={350} label="MED" />
                <PriceBox price={400} label="4WD" />
              </div>
            </div>
            <button 
              onClick={() => scrollToSection("booking", "pc")}
              className="w-full bg-lr-red text-white font-barlow font-bold py-3 text-lg uppercase tracking-[0.15em] hover:bg-red-700 transition-colors"
              data-testid="book-paint-correction"
            >
              BOOK ONLINE
            </button>
          </div>

          {/* CERAMIC COATING Card */}
          <div className="bg-lr-card border border-lr-border rounded-sm overflow-hidden" data-testid="service-card-ceramic-coating">
            <div className="p-5 md:p-6">
              <h3 className="font-barlow text-2xl md:text-3xl text-center tracking-wide">
                <span className="font-black text-lr-red">CERAMIC </span>
                <span className="font-black text-white">COATING</span>
              </h3>
              <p className="text-lr-red font-barlow font-bold text-center text-xs tracking-[0.2em] mt-1 mb-4">
                4 YEAR PROTECTION
              </p>
              
              <ul className="space-y-1.5 mb-4">
                <Feature text="Long-Lasting Ceramic Protection" />
                <Feature text="Superior Paint Gloss & Depth" />
                <Feature text="Hydrophobic Water Repellent Finish" />
                <Feature text="UV & Chemical Resistance" />
                <Feature text="Full Detail Service Included" />
              </ul>
              
              <div className="flex justify-center">
                <div className="border-2 border-lr-red px-10 py-2 text-center">
                  <div className="font-barlow font-black text-white leading-none">
                    <span className="text-base align-top">$</span>
                    <span className="text-3xl">600</span>
                  </div>
                  <div className="font-barlow font-bold text-xs text-lr-silver mt-1">ALL SIZES</div>
                </div>
              </div>
            </div>
            <button 
              onClick={() => scrollToSection("booking", "cc")}
              className="w-full bg-lr-red text-white font-barlow font-bold py-3 text-lg uppercase tracking-[0.15em] hover:bg-red-700 transition-colors"
              data-testid="book-ceramic-coating"
            >
              BOOK ONLINE
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ scrollToSection }) => {
  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-lr-black to-lr-card" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-wide">
          READY TO EXPERIENCE THE<br />
          <span className="text-lr-red">DETAILING DIFFERENCE?</span>
        </h2>
        
        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-10">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-lr-red" size={20} />
            <span className="text-lr-silver font-manrope">Flexible scheduling options</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="text-lr-red" size={20} />
            <span className="text-lr-silver font-manrope">Premium service guarantee</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="text-lr-red" size={20} />
            <span className="text-lr-silver font-manrope">100% satisfaction guaranteed</span>
          </div>
        </div>
        
        <Button 
          size="lg"
          onClick={() => scrollToSection("booking")}
          className="bg-lr-red hover:bg-red-700 text-white font-barlow uppercase tracking-wider px-12 py-6 text-lg btn-glow"
          data-testid="cta-book-now"
        >
          Book Your Detail Today
          <ChevronRight className="ml-2" size={20} />
        </Button>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = ({ selectedService, setSelectedService }) => {
  const [step, setStep] = useState(2);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingVehicleType, setBookingVehicleType] = useState("small");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    vehicleMake: "",
    vehicleModel: "",
    notes: "",
    promoCode: ""
  });
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Reset booking flow when service changes
  useEffect(() => {
    if (selectedService) {
      setStep(2);
    }
  }, [selectedService]);

  // Fetch available times when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableTimes(selectedDate);
    }
  }, [selectedDate]);

  const [allSlots, setAllSlots] = useState([]);

  const fetchAvailableTimes = async (date) => {
    setIsLoading(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/available-times?date_str=${dateStr}`);
      setAvailableSlots(response.data.available_slots);
      setAllSlots(response.data.all_slots || []);
    } catch (error) {
      console.error("Error fetching available times:", error);
      toast.error("Failed to fetch available times");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectService = (service) => {
    setSelectedService(service);
    setStep(2);
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep(3);
  };

  const handleSelectTime = (time) => {
    setSelectedTime(time);
    setStep(4);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    if (!selectedService) {
      toast.error("Please select a service");
      return;
    }

    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const addonsTotal = getAddonsTotal();
      const bookingData = {
        service_id: selectedService.id,
        service_name: selectedService.name,
        service_price: getFinalPrice(),
        original_price: selectedService.price,
        promo_code: promoApplied ? formData.promoCode.toUpperCase() : null,
        discount_applied: promoApplied ? (selectedService.price - discountedPrice) : 0,
        addons: selectedAddons.map(id => ADDONS.find(a => a.id === id)?.name).filter(Boolean),
        addons_total: addonsTotal,
        booking_date: format(selectedDate, "yyyy-MM-dd"),
        booking_time: selectedTime,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_address: formData.address,
        vehicle_make: formData.vehicleMake || null,
        vehicle_model: formData.vehicleModel || null,
        notes: formData.notes || null
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      setConfirmedBooking(response.data);
      setBookingConfirmed(true);
      toast.success("Booking confirmed!");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setIsLoading(false);
    }
  };

  const resetBooking = () => {
    setStep(2);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setBookingVehicleType("small");
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      vehicleMake: "",
      vehicleModel: "",
      notes: "",
      promoCode: ""
    });
    setPromoApplied(false);
    setPromoError("");
    setDiscountedPrice(null);
    setSelectedAddons([]);
    setBookingConfirmed(false);
    setConfirmedBooking(null);
  };

  const applyPromoCode = () => {
    const code = formData.promoCode.toUpperCase();
    if (code === "PLUS10" || code === "SECRET10") {
      const discount = selectedService.price * 0.10;
      setDiscountedPrice(selectedService.price - discount);
      setPromoApplied(true);
      setPromoError("");
      toast.success("Promo code applied! 10% off");
    } else {
      setPromoApplied(false);
      setDiscountedPrice(null);
      setPromoError("Invalid promo code");
    }
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]
    );
  };

  const getAddonsTotal = () => {
    return selectedAddons.reduce((sum, id) => {
      const addon = ADDONS.find(a => a.id === id);
      if (!addon) return sum;
      if (addon.prices) return sum + (addon.prices[bookingVehicleType] || 0);
      return sum + (addon.price || 0);
    }, 0);
  };

  const getFinalPrice = () => {
    const basePrice = promoApplied ? discountedPrice : selectedService?.price || 0;
    return basePrice + getAddonsTotal();
  };

  const disabledDays = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  if (bookingConfirmed && confirmedBooking) {
    return (
      <section id="booking" className="py-20 md:py-32 bg-lr-card" data-testid="booking-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-lr-black border border-lr-border rounded-sm p-8 md:p-12">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-500" size={40} />
            </div>
            <h2 className="font-barlow text-3xl md:text-4xl font-bold text-white mb-4 tracking-wide">
              BOOKING CONFIRMED
            </h2>
            <p className="text-lr-silver font-manrope mb-8">
              Thank you for choosing LR Detail. We'll see you soon!
            </p>
            
            <div className="bg-lr-card border border-lr-border rounded-sm p-6 text-left mb-8">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-lr-silver">Service:</span>
                  <span className="text-white font-semibold">{confirmedBooking.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-lr-silver">Date:</span>
                  <span className="text-white font-semibold">
                    {format(new Date(confirmedBooking.booking_date), "EEEE, MMMM d, yyyy")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-lr-silver">Time:</span>
                  <span className="text-white font-semibold">{confirmedBooking.booking_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-lr-silver">Price:</span>
                  <span className="text-lr-red font-bold text-xl">${confirmedBooking.service_price}</span>
                </div>
                <div className="border-t border-lr-border pt-4">
                  <span className="text-lr-silver">Location:</span>
                  <p className="text-white mt-1">{confirmedBooking.customer_address}</p>
                </div>
              </div>
            </div>

            <p className="text-green-500 text-sm font-manrope mb-6">
              ✓ Confirmation email sent to {confirmedBooking.customer_email}
            </p>

            <Button 
              onClick={resetBooking}
              className="bg-lr-red hover:bg-red-700 text-white font-barlow uppercase tracking-wider px-8"
              data-testid="book-another"
            >
              Book Another Service
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-20 md:py-32 bg-lr-card" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            BOOK YOUR DETAIL
          </h2>
          <p className="text-lr-silver font-manrope text-base md:text-lg">
            Select your service, pick a time, and we'll come to you
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {["Date", "Time", "Details"].map((label, index) => {
            const stepNum = index + 2;
            return (
            <div key={label} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                step > stepNum ? "bg-lr-red border-lr-red" : 
                step === stepNum ? "border-lr-red text-lr-red" : "border-lr-border text-lr-silver"
              }`}>
                {step > stepNum ? (
                  <CheckCircle className="text-white" size={20} />
                ) : (
                  <span className="font-barlow font-semibold">{index + 1}</span>
                )}
              </div>
              <span className={`ml-2 font-manrope text-sm hidden sm:inline ${
                step >= stepNum ? "text-white" : "text-lr-silver"
              }`}>
                {label}
              </span>
              {index < 2 && (
                <div className={`w-8 md:w-16 h-0.5 mx-2 md:mx-4 ${
                  step > stepNum ? "bg-lr-red" : "bg-lr-border"
                }`} />
              )}
            </div>
            );
          })}
        </div>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <div data-testid="booking-step-1">
            {/* Vehicle Type Selector */}
            <p className="text-center text-lr-silver font-manrope mb-4">Select your vehicle size:</p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {VEHICLE_TYPES.map((vehicle) => {
                const vehicleIcons = {
                  small: Car,
                  mid: CarFront,
                  xl: Truck
                };
                const VehicleIcon = vehicleIcons[vehicle.id];
                
                return (
                  <button
                    key={vehicle.id}
                    onClick={() => setBookingVehicleType(vehicle.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-sm border transition-all font-manrope text-sm ${
                      bookingVehicleType === vehicle.id
                        ? "bg-lr-red border-lr-red text-white"
                        : "bg-lr-card border-lr-border text-lr-silver hover:border-lr-red"
                    }`}
                  >
                    <VehicleIcon size={18} />
                    <span className="font-barlow font-semibold">{vehicle.name}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {SERVICES_DATA[bookingVehicleType].map((service) => {
                const icons = { "wv": Droplets, "fd": Sparkles, "pc": Car, "cc": Shield };
                const iconKey = service.id.split('-')[0];
                const Icon = icons[iconKey] || Sparkles;
                return (
                  <Card 
                    key={service.id}
                    className={`cursor-pointer transition-all hover:border-lr-red ${
                      selectedService?.id === service.id ? "border-lr-red bg-lr-red/10" : "bg-lr-black border-lr-border"
                    }`}
                    onClick={() => handleSelectService({
                      ...service,
                      vehicleType: VEHICLE_TYPES.find(v => v.id === bookingVehicleType)?.name
                    })}
                    data-testid={`booking-service-${service.id}`}
                  >
                    <CardContent className="p-6 text-center">
                      <Icon className="mx-auto mb-4 text-lr-red" size={32} />
                      <h3 className="font-barlow text-lg font-semibold text-white mb-2">{service.name}</h3>
                      <p className="font-barlow text-2xl font-bold text-lr-red">${service.price}</p>
                      <p className="text-lr-silver/60 text-xs mt-1">{VEHICLE_TYPES.find(v => v.id === bookingVehicleType)?.name}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Select Date */}
        {step === 2 && (
          <div className="flex flex-col items-center" data-testid="booking-step-2">
            {selectedService && (
              <Card className="bg-lr-black border-lr-border p-4 mb-6">
                <p className="text-white font-barlow text-lg text-center">{selectedService.name} - <span className="text-lr-red font-bold">${selectedService.price}</span></p>
              </Card>
            )}
            <div className="bg-lr-black border border-lr-border rounded-sm p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleSelectDate}
                disabled={disabledDays}
                className="text-white"
                data-testid="booking-calendar"
              />
            </div>
            
            <p className="text-lr-silver text-sm font-manrope mt-4 text-center">
              <Clock className="inline mr-2" size={16} />
              Available 4pm-8pm Weekdays | 7am-8pm Weekends
            </p>

            <Button 
              variant="outline" 
              onClick={() => setStep(1)}
              className="mt-6 border-lr-border text-lr-silver hover:text-white"
              data-testid="booking-back-to-service"
            >
              Back to Services
            </Button>
          </div>
        )}

        {/* Step 3: Select Time */}
        {step === 3 && selectedDate && (
          <div className="text-center" data-testid="booking-step-3">
            <Card className="bg-lr-black border-lr-border p-4 mb-6 inline-block">
              <p className="text-lr-silver font-manrope text-sm mb-1">
                {format(selectedDate, "EEEE, MMMM d")}
              </p>
            </Card>

            {isLoading ? (
              <div className="text-lr-silver">Loading available times...</div>
            ) : allSlots.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 max-w-2xl mx-auto">
                {allSlots.map((slot) => (
                  <button
                    key={slot.time}
                    onClick={() => slot.available && handleSelectTime(slot.time)}
                    disabled={!slot.available}
                    className={`time-slot p-3 border rounded-sm font-manrope text-sm transition-all ${
                      selectedTime === slot.time 
                        ? "selected" 
                        : slot.available
                          ? "border-lr-border text-lr-silver hover:text-white"
                          : "border-lr-border/50 bg-lr-card/50 cursor-not-allowed"
                    }`}
                    data-testid={`time-slot-${slot.time}`}
                  >
                    {slot.available ? (slot.time_display || slot.time) : (
                      <span className="text-lr-silver/50">
                        <span className="block">{slot.time_display || slot.time}</span>
                        <span className="text-xs text-lr-red/70">Unavailable</span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 max-w-2xl mx-auto">
                {availableSlots.map((time) => (
                  <button
                    key={time}
                    onClick={() => handleSelectTime(time)}
                    className={`time-slot p-3 border rounded-sm font-manrope text-sm transition-all ${
                      selectedTime === time 
                        ? "selected" 
                        : "border-lr-border text-lr-silver hover:text-white"
                    }`}
                    data-testid={`time-slot-${time}`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-lr-silver">No available times for this date. Please select another date.</p>
            )}

            <Button 
              variant="outline" 
              onClick={() => setStep(2)}
              className="mt-8 border-lr-border text-lr-silver hover:text-white"
              data-testid="booking-back-to-date"
            >
              Back to Calendar
            </Button>
          </div>
        )}

        {/* Step 4: Enter Details */}
        {step === 4 && selectedTime && (
          <div className="max-w-xl mx-auto" data-testid="booking-step-4">
            <Card className="bg-lr-black border-lr-border p-4 mb-8">
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="text-lr-silver">{format(selectedDate, "MMM d, yyyy")}</span>
                <span className="text-lr-border">|</span>
                <span className="text-lr-red font-semibold">{selectedTime}</span>
              </div>
            </Card>

            {/* Service Selection */}
            <div className="mb-6">
              <Label className="text-lr-silver font-manrope mb-3 block">Select Service *</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {VEHICLE_TYPES.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    type="button"
                    onClick={() => {
                      setBookingVehicleType(vehicle.id);
                      setSelectedService(null);
                      setPromoApplied(false);
                      setDiscountedPrice(null);
                    }}
                    className={`px-4 py-2 border rounded-sm font-barlow text-sm transition-all ${
                      bookingVehicleType === vehicle.id
                        ? "bg-lr-red border-lr-red text-white"
                        : "border-lr-border text-lr-silver hover:border-lr-red"
                    }`}
                    data-testid={`booking-vehicle-${vehicle.id}`}
                  >
                    {vehicle.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-2">
                {SERVICES_DATA[bookingVehicleType].map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => handleSelectService(service)}
                    className={`flex items-center justify-between p-3 border rounded-sm transition-all ${
                      selectedService?.id === service.id
                        ? "border-lr-red bg-lr-red/10 text-white"
                        : "border-lr-border text-lr-silver hover:border-lr-red"
                    }`}
                    data-testid={`booking-service-${service.id}`}
                  >
                    <span className="font-barlow font-semibold">{service.name}</span>
                    <span className="font-barlow font-bold text-lr-red">${service.price}</span>
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmitBooking} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-lr-silver font-manrope">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-lr-card border-lr-border text-white mt-2"
                    placeholder="John Smith"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-lr-silver font-manrope">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-lr-card border-lr-border text-white mt-2"
                    placeholder="john@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-lr-silver font-manrope">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="bg-lr-card border-lr-border text-white mt-2"
                  placeholder="0400 000 000"
                  data-testid="input-phone"
                />
              </div>

              <div>
                <Label htmlFor="address" className="text-lr-silver font-manrope">Service Address *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="bg-lr-card border-lr-border text-white mt-2"
                  placeholder="123 Example St, Rydalmere NSW 2116"
                  data-testid="input-address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="vehicleMake" className="text-lr-silver font-manrope">Vehicle Make</Label>
                  <Input
                    id="vehicleMake"
                    name="vehicleMake"
                    value={formData.vehicleMake}
                    onChange={handleInputChange}
                    className="bg-lr-card border-lr-border text-white mt-2"
                    placeholder="Toyota"
                    data-testid="input-vehicle-make"
                  />
                </div>
                <div>
                  <Label htmlFor="vehicleModel" className="text-lr-silver font-manrope">Vehicle Model</Label>
                  <Input
                    id="vehicleModel"
                    name="vehicleModel"
                    value={formData.vehicleModel}
                    onChange={handleInputChange}
                    className="bg-lr-card border-lr-border text-white mt-2"
                    placeholder="Camry"
                    data-testid="input-vehicle-model"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="text-lr-silver font-manrope">Additional Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="bg-lr-card border-lr-border text-white mt-2 min-h-[100px]"
                  placeholder="Any special requests or parking instructions..."
                  data-testid="input-notes"
                />
              </div>

              {/* Add-ons */}
              <div className="bg-lr-card border border-lr-border rounded-sm p-4">
                <Label className="text-lr-silver font-manrope mb-3 block">Add-ons</Label>
                {ADDONS.map((addon) => (
                  <button
                    key={addon.id}
                    type="button"
                    onClick={() => toggleAddon(addon.id)}
                    className={`w-full flex items-center justify-between p-3 border rounded-sm transition-all mb-2 ${
                      selectedAddons.includes(addon.id)
                        ? "border-lr-red bg-lr-red/10 text-white"
                        : "border-lr-border text-lr-silver hover:border-lr-red"
                    }`}
                    data-testid={`addon-${addon.id}`}
                  >
                    <div className="text-left">
                      <span className="font-barlow font-semibold block">{addon.name}</span>
                      <span className="text-xs text-lr-silver/60 font-manrope">{addon.description}</span>
                    </div>
                    <span className="font-barlow font-bold text-lr-red ml-4 shrink-0">+${addon.prices ? addon.prices[bookingVehicleType] : addon.price}</span>
                  </button>
                ))}
              </div>

              {/* Promo Code */}
              <div className="bg-lr-card border border-lr-border rounded-sm p-4">
                <Label htmlFor="promoCode" className="text-lr-silver font-manrope">Promo Code</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="promoCode"
                    name="promoCode"
                    value={formData.promoCode}
                    onChange={handleInputChange}
                    className="bg-lr-black border-lr-border text-white uppercase"
                    placeholder="Enter promo code"
                    data-testid="input-promo-code"
                  />
                  <Button 
                    type="button"
                    onClick={applyPromoCode}
                    className="bg-lr-border hover:bg-lr-red text-white font-barlow uppercase tracking-wider"
                    data-testid="apply-promo"
                  >
                    Apply
                  </Button>
                </div>
                {promoError && <p className="text-red-500 text-sm mt-2">{promoError}</p>}
                {promoApplied && (
                  <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded-sm">
                    <p className="text-green-500 text-sm font-manrope">
                      ✓ Promo code PLUS10 applied! 10% off
                    </p>
                    <p className="text-lr-silver text-sm mt-1">
                      <span className="line-through">${selectedService.price}</span>
                      <span className="text-green-500 font-bold ml-2">${discountedPrice.toFixed(2)}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setStep(3)}
                  className="border-lr-border text-lr-silver hover:text-white flex-1"
                  data-testid="booking-back-to-time"
                >
                  Back
                </Button>
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="bg-lr-red hover:bg-red-700 text-white font-barlow uppercase tracking-wider flex-1 btn-glow"
                  data-testid="confirm-booking"
                >
                  {isLoading ? "Confirming..." : selectedService ? `Confirm Booking - $${getFinalPrice().toFixed(2)}` : "Select a service"}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  return (
    <section id="gallery" className="py-20 md:py-32 bg-lr-black" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            OUR WORK
          </h2>
          <p className="text-lr-silver font-manrope text-base md:text-lg">
            See the quality results we deliver
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {IMAGES.gallery.map((img, index) => (
            <div 
              key={index}
              className={`overflow-hidden rounded-sm ${index === 0 ? "col-span-2 row-span-2" : ""}`}
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={img} 
                alt={`Car detailing work ${index + 1}`}
                className="gallery-image w-full h-full object-cover aspect-square"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Data
const FAQ_DATA = [
  {
    question: "Do you come to my home or office?",
    answer: "Yes we're a mobile detailing service and bring everything needed."
  },
  {
    question: "How long does a detail take?",
    answer: "Between 1–3 hours depending on the package and car condition."
  },
  {
    question: "Do I need to supply water or power?",
    answer: "Please ensure water and power are available and easily accessible."
  },
  {
    question: "What areas do you service?",
    answer: "Rydalmere, Dundas, Parramatta, North Parramatta, Ermington, Westmead and nearby suburbs."
  },
  {
    question: "What payment methods do you accept?",
    answer: "Cash and PayID accepted. Credit card payments coming soon."
  }
];

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 md:py-32 bg-lr-card" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-wide">
              ABOUT US –<br />
              <span className="text-lr-red">LR DETAIL SYDNEY</span>
            </h2>
            <p className="text-lr-silver font-manrope text-base md:text-lg mb-6 leading-relaxed">
              LR Detail Sydney is a car detailing business based in Rydalmere, founded and run by Lochlainn Melville-Ryan and Oliver Casamento. With a shared passion for car detailing, they decided to turn their interest into a business and provide high-quality detailing services to customers across Sydney.
            </p>
            <p className="text-lr-silver font-manrope text-base md:text-lg mb-8 leading-relaxed">
              What started as a hobby of detailing and maintaining vehicles quickly grew into a commitment to delivering professional results and attention to detail. Through LR Detail Sydney, Lochlainn Melville-Ryan and Oliver Casamento aim to provide reliable, high-quality car detailing while making it simple and convenient for customers to book and keep their vehicles looking their best.
            </p>
            
            <div className="space-y-6">
              {[
                { icon: Car, title: "Mobile Service", desc: "We come to your home or workplace" },
                { icon: Shield, title: "Quality Products", desc: "Professional-grade detailing products" },
                { icon: Clock, title: "Flexible Hours", desc: "Weekdays 4pm-8pm | Weekends 7am-8pm" }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="p-3 bg-lr-red/10 rounded-sm shrink-0">
                    <item.icon className="text-lr-red" size={24} />
                  </div>
                  <div>
                    <h3 className="font-barlow text-lg font-semibold text-white">{item.title}</h3>
                    <p className="text-lr-silver font-manrope text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_8f5d79e3-c8e6-419c-a9da-7e966a16ea6e/artifacts/z85ut533_IMG_1896.jpeg"
              alt="Lochlainn and Oliver - LR Detail Sydney"
              className="rounded-sm w-full object-cover aspect-[4/3]"
            />
            <div className="absolute -bottom-6 -left-6 bg-lr-red p-6 rounded-sm hidden md:block">
              <p className="font-barlow text-4xl font-bold text-white">100%</p>
              <p className="font-manrope text-sm text-white/80">Satisfaction</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Google Reviews Section
const ReviewsSection = () => {
  return (
    <section id="reviews" className="py-20 md:py-32 bg-lr-card" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            CUSTOMER REVIEWS
          </h2>
          <p className="text-lr-silver font-manrope text-base md:text-lg">
            See what our customers say about us
          </p>
        </div>
        
        <div className="elfsight-app-aaefb34e-cf08-4139-888b-b40bf2b0359b" data-elfsight-app-lazy></div>
      </div>
    </section>
  );
};

// Instagram Feed Section
const InstagramSection = () => {
  useEffect(() => {
    // Load Instagram embed script
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <section id="instagram" className="py-20 md:py-32 bg-lr-black" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            FOLLOW US ON <span className="text-pink-500">INSTAGRAM</span>
          </h2>
          <p className="text-lr-silver font-manrope text-base md:text-lg">
            @lrdetailsydney
          </p>
        </div>
        
        <div className="flex justify-center">
          <blockquote 
            className="instagram-media" 
            data-instgrm-permalink="https://www.instagram.com/lrdetailsydney" 
            data-instgrm-version="12" 
            style={{ 
              background: '#FFF', 
              border: 0, 
              borderRadius: '3px', 
              boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)', 
              margin: '1px', 
              maxWidth: '540px', 
              minWidth: '326px', 
              padding: 0, 
              width: '99.375%' 
            }}
          >
            <div style={{ padding: '16px' }}>
              <a 
                href="https://www.instagram.com/lrdetailsydney" 
                style={{ 
                  background: '#FFFFFF', 
                  lineHeight: 0, 
                  padding: '0 0', 
                  textAlign: 'center', 
                  textDecoration: 'none', 
                  width: '100%' 
                }} 
                target="_blank"
                rel="noopener noreferrer"
              >
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <div style={{ backgroundColor: '#F4F4F4', borderRadius: '50%', flexGrow: 0, height: '40px', marginRight: '14px', width: '40px' }}></div>
                  <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'center' }}>
                    <div style={{ backgroundColor: '#F4F4F4', borderRadius: '4px', flexGrow: 0, height: '14px', marginBottom: '6px', width: '100px' }}></div>
                    <div style={{ backgroundColor: '#F4F4F4', borderRadius: '4px', flexGrow: 0, height: '14px', width: '60px' }}></div>
                  </div>
                </div>
                <div style={{ padding: '19% 0' }}></div>
                <div style={{ display: 'block', height: '50px', margin: '0 auto 12px', width: '50px' }}>
                  <Instagram className="text-pink-500" size={50} />
                </div>
                <div style={{ paddingTop: '8px' }}>
                  <div style={{ color: '#3897f0', fontFamily: 'Arial,sans-serif', fontSize: '14px', fontStyle: 'normal', fontWeight: 550, lineHeight: '18px' }}>
                    View this profile on Instagram
                  </div>
                </div>
              </a>
            </div>
          </blockquote>
        </div>
        
        <div className="text-center mt-8">
          <a 
            href="https://www.instagram.com/lrdetailsydney" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-barlow uppercase tracking-wider px-8 py-3 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Instagram size={20} />
            Follow @lrdetailsydney
          </a>
        </div>
      </div>
    </section>
  );
};

// Equipment & Products Section
const EquipmentSection = () => {
  return (
    <section id="equipment" className="py-20 md:py-32 bg-lr-black" data-testid="equipment-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            OUR PROFESSIONAL<br />
            <span className="text-lr-red">EQUIPMENT & PRODUCTS</span>
          </h2>
          <p className="text-lr-silver font-manrope text-base md:text-lg max-w-3xl mx-auto">
            At LR Detail Sydney, we use high-quality tools and professional detailing products to ensure every vehicle receives a safe, deep clean and long-lasting protection.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Premium Cleaning Products */}
          <Card className="bg-lr-card border-lr-border p-6 lg:p-8">
            <CardContent className="p-0">
              <div className="p-3 bg-lr-red/10 rounded-sm w-fit mb-4">
                <Sparkles className="text-lr-red" size={28} />
              </div>
              <h3 className="font-barlow text-xl font-semibold text-white mb-4 tracking-wide">
                PREMIUM CLEANING PRODUCTS
              </h3>
              <p className="text-lr-silver font-manrope text-sm mb-4">
                We only use trusted automotive products designed specifically for car detailing, including:
              </p>
              <ul className="space-y-2 text-lr-silver font-manrope text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Professional ceramic wax protection for long-lasting shine</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>pH-safe foam washes for gentle but effective cleaning</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Specialised wheel cleaners to remove brake dust and grime</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Interior cleaners and protectants for dashboards, seats, and trims</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Glass cleaners for streak-free windows</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Tire shine and protectants to restore deep black finish</span>
                </li>
              </ul>
              <p className="text-lr-silver/70 font-manrope text-xs mt-4 italic">
                These products are safe for your vehicle's paint, plastics, wheels, and interior surfaces.
              </p>
            </CardContent>
          </Card>

          {/* Professional Detailing Equipment */}
          <Card className="bg-lr-card border-lr-border p-6 lg:p-8">
            <CardContent className="p-0">
              <div className="p-3 bg-lr-red/10 rounded-sm w-fit mb-4">
                <Shield className="text-lr-red" size={28} />
              </div>
              <h3 className="font-barlow text-xl font-semibold text-white mb-4 tracking-wide">
                PROFESSIONAL EQUIPMENT
              </h3>
              <p className="text-lr-silver font-manrope text-sm mb-4">
                Our detailing setup includes professional tools to deliver high-quality results:
              </p>
              <ul className="space-y-2 text-lr-silver font-manrope text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>High-pressure washer with foam cannon system</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Wet & dry vacuum for deep interior cleaning</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Drill powered brushes for upholstery and carpets</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Microfibre towels and drying towels to prevent scratches</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Wheel and detail brushes for hard-to-reach areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Air blower and jet tools for drying and dust removal</span>
                </li>
              </ul>
              <p className="text-lr-silver/70 font-manrope text-xs mt-4 italic">
                This equipment allows us to clean every part of your car thoroughly without damaging the surfaces.
              </p>
            </CardContent>
          </Card>

          {/* Clean & Protected Service */}
          <Card className="bg-lr-card border-lr-border p-6 lg:p-8">
            <CardContent className="p-0">
              <div className="p-3 bg-lr-red/10 rounded-sm w-fit mb-4">
                <Car className="text-lr-red" size={28} />
              </div>
              <h3 className="font-barlow text-xl font-semibold text-white mb-4 tracking-wide">
                CLEAN & PROTECTED SERVICE
              </h3>
              <p className="text-lr-silver font-manrope text-sm mb-4">
                Every vehicle we detail also receives:
              </p>
              <ul className="space-y-2 text-lr-silver font-manrope text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Disposable steering wheel covers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Fresh, clean microfibre towels for each vehicle</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-lr-red shrink-0 mt-0.5" size={16} />
                  <span>Sanitised equipment between services</span>
                </li>
              </ul>
              <p className="text-lr-silver/70 font-manrope text-xs mt-4 italic">
                This ensures your vehicle stays hygienic and protected throughout the service.
              </p>
              
              <div className="mt-6 pt-6 border-t border-lr-border">
                <p className="text-white font-barlow text-lg mb-2">QUALITY GUARANTEED</p>
                <p className="text-lr-silver font-manrope text-sm">
                  We take pride in using only the best products and equipment to deliver outstanding results every time.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section id="faq" className="py-20 md:py-32 bg-lr-black" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            FAQ
          </h2>
          <p className="text-lr-silver font-manrope text-base md:text-lg">
            Answers to common questions
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {FAQ_DATA.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-lr-card border border-lr-border rounded-sm px-6 data-[state=open]:border-lr-red transition-colors"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-white font-barlow text-lg hover:text-lr-red hover:no-underline py-5">
                <div className="flex items-center gap-3 text-left">
                  <HelpCircle className="text-lr-red shrink-0" size={20} />
                  {faq.question}
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-lr-silver font-manrope text-base pb-5 pl-8">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-20 md:py-32 bg-lr-black" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            GET IN TOUCH
          </h2>
          <p className="text-lr-silver font-manrope text-base md:text-lg">
            Have questions? We're here to help
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
          <Card className="bg-lr-card border-lr-border text-center p-8 hover:border-lr-red transition-colors">
            <CardContent className="p-0">
              <div className="w-16 h-16 bg-lr-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="text-lr-red" size={28} />
              </div>
              <h3 className="font-barlow text-lg font-semibold text-white mb-2">CALL US</h3>
              <a 
                href="tel:0410188701" 
                className="text-lr-silver hover:text-lr-red font-manrope transition-colors"
                data-testid="contact-phone"
              >
                0410 188 701
              </a>
            </CardContent>
          </Card>

          <Card className="bg-lr-card border-lr-border text-center p-8 hover:border-lr-red transition-colors">
            <CardContent className="p-0">
              <div className="w-16 h-16 bg-lr-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-lr-red" size={28} />
              </div>
              <h3 className="font-barlow text-lg font-semibold text-white mb-2">EMAIL US</h3>
              <a 
                href="mailto:lrdetailsydney@gmail.com" 
                className="text-lr-silver hover:text-lr-red font-manrope transition-colors text-xs md:text-sm break-all"
                data-testid="contact-email"
              >
                lrdetailsydney<wbr/>@gmail.com
              </a>
            </CardContent>
          </Card>

          <Card className="bg-lr-card border-lr-border text-center p-8 hover:border-lr-red transition-colors">
            <CardContent className="p-0">
              <div className="w-16 h-16 bg-lr-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-lr-red" size={28} />
              </div>
              <h3 className="font-barlow text-lg font-semibold text-white mb-2">SERVICE AREA</h3>
              <p className="text-lr-silver font-manrope" data-testid="contact-location">
                Rydalmere & Greater Sydney
              </p>
            </CardContent>
          </Card>

          <Card className="bg-lr-card border-lr-border text-center p-8 hover:border-pink-500 transition-colors">
            <CardContent className="p-0">
              <div className="w-16 h-16 bg-pink-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Instagram className="text-pink-500" size={28} />
              </div>
              <h3 className="font-barlow text-lg font-semibold text-white mb-2">FOLLOW US</h3>
              <a 
                href="https://www.instagram.com/lrdetailsydney" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-lr-silver hover:text-pink-500 font-manrope transition-colors text-sm"
                data-testid="contact-instagram"
              >
                @lrdetailsydney
              </a>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-12">
          <p className="text-lr-silver font-manrope mb-4">
            <Clock className="inline mr-2" size={18} />
            Hours: Weekdays 4pm-8pm | Weekends 7am-8pm
          </p>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const navigate = useNavigate();
  
  return (
    <footer className="bg-lr-card border-t border-lr-border py-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="LR Detail" className="h-8 w-auto" />
            <span className="text-lr-silver font-manrope text-sm">
              © {new Date().getFullYear()} LR Detail Sydney. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <button 
              onClick={() => navigate('/terms')}
              className="text-lr-silver hover:text-white transition-colors text-sm font-manrope"
              data-testid="footer-terms"
            >
              Terms & Conditions
            </button>
            <a href="tel:0410188701" className="text-lr-silver hover:text-white transition-colors">
              <Phone size={20} />
            </a>
            <a href="mailto:lrdetailsydney@gmail.com" className="text-lr-silver hover:text-white transition-colors">
              <Mail size={20} />
            </a>
            <a href="https://www.instagram.com/lrdetailsydney" target="_blank" rel="noopener noreferrer" className="text-lr-silver hover:text-pink-500 transition-colors" data-testid="footer-instagram">
              <Instagram size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Terms & Conditions Page
const TermsPage = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-lr-black" data-testid="terms-page">
      {/* Header */}
      <div className="bg-lr-card border-b border-lr-border py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-lr-silver hover:text-white transition-colors"
            data-testid="terms-back"
          >
            <ArrowLeft size={20} />
            <span className="font-manrope">Back to Home</span>
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center mb-12">
          <FileText className="text-lr-red mx-auto mb-4" size={48} />
          <h1 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-wide">
            TERMS & CONDITIONS
          </h1>
          <p className="text-lr-silver font-manrope">LR Detail Sydney</p>
        </div>
        
        <div className="space-y-10 text-lr-silver font-manrope">
          {/* Understanding Our Policy */}
          <section>
            <h2 className="font-barlow text-xl md:text-2xl font-semibold text-white mb-4">Understanding Our Policy</h2>
            <p>Accepting any service through LR Detail Sydney means they have understood and accepted the terms and conditions through this website.</p>
          </section>
          
          {/* Cancellations & Rescheduling */}
          <section>
            <h2 className="font-barlow text-xl md:text-2xl font-semibold text-white mb-4">Cancellations & Rescheduling</h2>
            <p className="mb-4">LR Detail Sydney understand that sometimes schedule adjustments are necessary. As a common courtesy to everyone, we would greatly appreciate a 48-hour (2 days) notice prior to cancellations or rescheduling of appointments. The cancellation/rescheduling policy gives us time to inform our waitlist clients of any availability. In this way, another customer may have an opportunity to grab your existing time slot.</p>
            
            <p className="mb-4 text-lr-red font-semibold">Please note – Deposit is non refundable in case you want to cancel.</p>
            
            <p className="mb-4">In the case of booking an appointment within 24 hours and you plan to cancel or reschedule your booking, we require 12-hour notice, otherwise additional charges may apply.</p>
            
            <p className="mb-4">In the case of booking an appointment within 48 hours and you plan to cancel or reschedule your booking, we require 24-hour (1 day) notice, otherwise additional charges may apply.</p>
            
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>48 hours before the due time will not incur any additional charges.</li>
              <li>24 hours before the due time will incur 50% of the service fee to the agreed price.</li>
              <li>12 hours before the due time or on the day will incur 80% of the service fees of the service cost.</li>
              <li>In case of bad weather condition and wish to reschedule on the day, you will not be charged additional fees.</li>
            </ul>
            
            <p className="mb-4">Failure to be present at the location and time of the booked service will result in the cancellation of the appointment by LR Detail Sydney and will be considered a cancellation policy violation and the full value of the booked service will be charged.</p>
            
            <p className="mb-4">LR Detail Sydney reserves the right to take full payment if a booking is cancelled or rescheduled on the day of appointment without prior agreement.</p>
            
            <p>LR Detail Sydney reserves the rights to cancel or reschedule a booking time/date with customers due to bad weather conditions or mechanical issues or equipment failure without any penalty to the company.</p>
          </section>
          
          {/* Liability */}
          <section>
            <h2 className="font-barlow text-xl md:text-2xl font-semibold text-white mb-4">Liability</h2>
            <p className="mb-4">LR Detail Sydney is not liable for any form of pollutants washed off the customers vehicle while detailing their vehicle that are left on the property that could or may have stained or damaged the driveway or lawn in anyways. All detailing products used by LR Detail Sydney are environmentally friendly and tested to ensure they are safe for the environment. LR Detail Sydney is liable for any physical damage done to the customer's property such as structural damage, that may have occurred while operating a motor vehicle on the property.</p>
            
            <p className="mb-4">LR Detail Sydney does not take liability or responsibility of any vehicle which already had problems before commencing the work such as scratches, scuffs, dents, swirl marks or any accident damages to the body as they may have been there prior to work commencing when the vehicle was dirty, and they could not be seen.</p>
            
            <p>Booking a service is through LR Detail Sydney meaning that the customers fully understand what our services are involved, and they fully accept our term and conditions.</p>
          </section>
          
          {/* Payment */}
          <section>
            <h2 className="font-barlow text-xl md:text-2xl font-semibold text-white mb-4">Payment</h2>
            <p className="mb-4">LR Detail Sydney reserves the right to charge each vehicle according to its condition and the type of vehicle.</p>
            
            <p className="mb-4">We accept Cash, Bank Transfer (PayID)</p>
            
            <p className="mb-4">Payment for all services must be paid on completion of work carried out prior to the operator leaving the job.</p>
            
            <p>If no payment is made LR Detail Sydney has the right to hold the vehicle key till full payment is made.</p>
          </section>
          
          {/* Refund Policy */}
          <section>
            <h2 className="font-barlow text-xl md:text-2xl font-semibold text-white mb-4">Refund Policy</h2>
            <p>Once the service is completed the customer is given chance to inspect the vehicle and complete the sale. Once the detailing service has completed the customer gets legally bind to pay our company. We give customers enough time to assess our work and do the payment. Once the sale is completed customers can't get any refund for the service already provided. This is a service industry and once the service is already provided and completed no refund for change of mind or unsatisfactory work. After the detailer has left and issues are brought up will incur additional charges based on travel time and cause of problem.</p>
          </section>
          
          {/* Service Guarantee */}
          <section>
            <h2 className="font-barlow text-xl md:text-2xl font-semibold text-white mb-4">Service Guarantee</h2>
            <p>LR Detail Sydney provides a professional service guaranteed. We would like to take the opportunity to thank you for considering LR Detail Sydney for your car and taking the time to read our policies listed above.</p>
          </section>
        </div>
        
        {/* Back to Home Button */}
        <div className="text-center mt-12">
          <Button 
            onClick={() => navigate('/')}
            className="bg-lr-red hover:bg-red-700 text-white font-barlow uppercase tracking-wider px-8"
            data-testid="terms-back-home"
          >
            Back to Home
          </Button>
        </div>
      </div>
      
      {/* Simple Footer */}
      <footer className="bg-lr-card border-t border-lr-border py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lr-silver font-manrope text-sm">
            © {new Date().getFullYear()} LR Detail Sydney. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#121212',
            color: '#fff',
            border: '1px solid #2A2A2A'
          }
        }}
      />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
    </BrowserRouter>
  );
}

// Home Page Component
function HomePage() {
  const [selectedService, setSelectedService] = useState(null);

  const scrollToSection = useCallback((sectionId, servicePrefix) => {
    if (servicePrefix) {
      // Pre-select the service for the default vehicle type (small)
      const service = SERVICES_DATA["small"].find(s => s.id.startsWith(servicePrefix + "-"));
      if (service) {
        setSelectedService(service);
      }
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  }, []);

  const handleSelectService = (service) => {
    setSelectedService(service);
    scrollToSection("contact");
  };

  return (
    <div className="min-h-screen bg-lr-black" data-testid="app-container">
      <Navigation scrollToSection={scrollToSection} />
      <HeroSection scrollToSection={scrollToSection} />
      <ServicesSection scrollToSection={scrollToSection} />
      <BookingSection 
        selectedService={selectedService} 
        setSelectedService={setSelectedService}
      />
      <CTASection scrollToSection={scrollToSection} />
      <AboutSection />
      <InstagramSection />
      <EquipmentSection />
      <FAQSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
